import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { RowBox } from '@components/shared/RowBox'
import { toast } from '@utils/functions/notificationToast'

import { WEEK_STATUS_CONFIG } from './statusConfig'
import { WeekStatusCard } from './WeekStatusCard'
import { getVisibleWeeks } from './helpers/getVisibleWeeks'
import { getWeekCardState } from './helpers/getWeekCardState'

import type { WeeklyStatusItemProps } from '@services/api/previewManagement/types'
import type { WeekRelationSectionProps } from './WeekStatusRelationModal/types'
import type { WeekStatusCardData, WeekStatusCardsProps, WeekStatusItemProps } from './types'

import * as apiPreviewManagement from '@services/api/previewManagement'

const buildStatuses = (week: WeeklyStatusItemProps): WeekStatusItemProps[] => [
  { code: 'not_started', label: 'Não Iniciada', count: week.notStarted },
  { code: 'in_progress', label: 'Em Andamento', count: week.inProgress },
  { code: 'pending_approval', label: 'Em Aprovação', count: week.pendingApproval },
  { code: 'completed', label: 'Finalizada', count: week.completed },
  { code: 'auto_send', label: 'Envio Automático', count: week.automaticSendCount }
]

export const WeekStatusCards = ({
  referenceDate,
  filters,
  onOpenWeekRelation,
  onActiveWeekChange,
  onSearchResult
}: WeekStatusCardsProps) => {
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null)
  const [loadingWeek, setLoadingWeek] = useState<number | null>(null)

  const { data, isSuccess } = useQuery({
    queryKey: ['preview-management-weekly-status', referenceDate, filters],
    queryFn: () => apiPreviewManagement.getWeeklyStatus({ referenceDate: referenceDate!, ...filters }),
    enabled: !!referenceDate
  })

  useEffect(() => {
    if (!isSuccess) return
    onSearchResult?.(!!data)
  }, [isSuccess, data])

  const cards: WeekStatusCardData[] = getVisibleWeeks(data?.weeks).map((week) => ({
    weekNumber: week.weekNumber,
    totalUnits: week.totalBranches,
    isCurrentPreview: week.isCurrentPreview,
    statuses: buildStatuses(week)
  }))

  const currentWeek = cards.find((card) => card.isCurrentPreview)?.weekNumber ?? cards[cards.length - 1]?.weekNumber ?? null
  const activeWeek = selectedWeek ?? currentWeek

  useEffect(() => {
    onActiveWeekChange?.(activeWeek)
  }, [activeWeek])

  const handleViewUnits = async (weekNumber: number, statuses: WeekStatusItemProps[]) => {
    if (!referenceDate) return

    setLoadingWeek(weekNumber)

    try {
      const [branchesResponse, autoSendResponse] = await Promise.all([
        apiPreviewManagement.getWeeklyStatusBranches({ referenceDate, weekNumber, ...filters }),
        apiPreviewManagement.getWeeklyStatusAutomaticSends({ referenceDate, weekNumber, ...filters })
      ])

      const sections: WeekRelationSectionProps[] = statuses.map((status) => {
        const config = WEEK_STATUS_CONFIG[status.code]

        const units =
          status.code === 'auto_send'
            ? autoSendResponse.branches.map((branch) => branch.branchDescription)
            : branchesResponse.rows
                .filter((branch) => branch.status === status.code)
                .map((branch) => branch.branchCodeDescription)

        return {
          code: status.code,
          label: status.label,
          color: config.color,
          background: config.background,
          icon: config.icon,
          count: status.count,
          units
        }
      })

      const totalUnits = sections.reduce((sum, section) => sum + section.units.length, 0)

      if (totalUnits === 0) {
        toast.warning('Nenhum dado encontrado no momento')
        return
      }

      onOpenWeekRelation({ weekNumber, sections })
    } catch (error) {
      toast.error('Falha ao buscar a relação de CLs da semana selecionada!')
    } finally {
      setLoadingWeek(null)
    }
  }

  return (
    <RowBox columns={5} gap={5} style={{ width: '80%', margin: '0 auto' }}>
      {cards.map((card) => {
        const { isCurrentWeek, isSelected } = getWeekCardState({
          cardWeekNumber: card.weekNumber,
          activeWeekNumber: activeWeek,
          isCurrentPreview: card.isCurrentPreview
        })

        return (
          <WeekStatusCard
            key={card.weekNumber}
            weekLabel={`Semana ${card.weekNumber}`}
            totalUnits={card.totalUnits}
            statuses={card.statuses}
            isCurrentWeek={isCurrentWeek}
            isSelected={isSelected}
            isLoadingUnits={loadingWeek === card.weekNumber}
            onSelect={() => setSelectedWeek(card.weekNumber)}
            onViewUnits={() => handleViewUnits(card.weekNumber, card.statuses)}
          />
        )
      })}
    </RowBox>
  )
}
